import React, { useEffect, useMemo, useState } from 'react';
import { Result, Progress, Space, Image, message } from 'antd';
import { useDispatch } from 'react-redux';
import { createTempDashboard, remove } from '@store/tempDashboard/actions';
import { DASHBOARD_TYPE, PAGE_TYPE, isAppModule } from '@src/utils/constants';
import { removeDashboard } from '@store/dashboards/remove/actions';
import { ProgressWrapper, Slide, StyledCarousel } from './styled';
import { setLandingPage } from '@api/landingPage';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import apptracker from '@src/utils/sensor/apptracker';
import { createSavedDashboardConfig, createTempDashboardConfig } from './migrationUtils';
import useSaveDashboard from '@components/saving/useSaveDashboard';
import { parseDashboardURL } from '@src/utils';
import { UpCircleOutlined } from '@ant-design/icons';
import IMG1 from '@images/layout/error-diag-trends.png';
import {
  ERROR_DIAG_MIGRATION,
  ERROR_DIAG_MIGRATION_ERROR,
  MIGRATED_ERROR_DIAG_DASHBOARD,
} from '@src/utils/sensor/track';
import { deleteSharedTab } from '@store/sharedTabs';
import { get } from 'lodash';
import { useNavigate } from 'react-router-dom';

enum CHOICE {
  new = 'new',
  legacy = 'legacy',
}

function NewErrorDashboard(props) {
  const { id, isSaved, isShared, base, fallback } = props;

  const dispatch = useDispatch();
  const { saveMutation } = useSaveDashboard();
  const [loadingPercent, setLoadingPercent] = useState(0);
  const navigate = useNavigate();

  // loading effects
  const increaseLoadingPercent = () => {
    setLoadingPercent((prevPercent) => {
      const newPercent = prevPercent + Math.floor(Math.random() * (25 - 10) + 10);
      if (newPercent > 90) {
        return 90;
      }
      return newPercent;
    });
  };

  const handleMigration = async () => {
    try {
      if (base) {
        if (isSaved) {
          // create new saved dashboard
          const savePayload = createSavedDashboardConfig(base);
          const { data } = await saveMutation.mutateAsync(savePayload);

          const tab = {
            id: data.id,
            dashboardTypeId: savePayload.dashboardTypeId,
          };

          //remove old one from db
          dispatch(
            removeDashboard(id, function* () {
              yield null;
            }),
          );

          // redirect to new dashboard
          setTimeout(() => {
            navigate(parseDashboardURL(tab));
            // dispatch(createSavedDashboard(data.id, data.dashboard, savePayload.dashboardTypeId, savePayload.config));
          }, 500);
        } else {
          // create temp dashboard for temp and shared compare dashboards
          const { config, dashboardName } = createTempDashboardConfig(base);

          setTimeout(() => {
            dispatch(
              createTempDashboard(
                isAppModule
                  ? DASHBOARD_TYPE.APP_EXPERIENCE_DRILLDOWN
                  : DASHBOARD_TYPE.TRENDS_DRILLDOWN,
                config,
                dashboardName,
              ),
            );
          }, 500);
        }

        // clear pulse 5 tab and localStorage
        setTimeout(() => {
          if (isShared) {
            dispatch(deleteSharedTab(id, false));
          } else {
            dispatch(remove(id, isSaved));
          }
        }, 600);

        apptracker(MIGRATED_ERROR_DIAG_DASHBOARD, { isSaved, id });
        setLoadingPercent(100);
      }
    } catch (err) {
      apptracker(ERROR_DIAG_MIGRATION_ERROR, { isSaved, id, err });
      fallback();
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      increaseLoadingPercent();
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // start migration process on mount
  useEffect(() => {
    handleMigration();
  }, []);

  return (
    <ProgressWrapper>
      Generating new dashboard...
      <Progress percent={loadingPercent} />
    </ProgressWrapper>
  );
}

function Migrate(props) {
  let { id } = props;
  const pageType = props.pageType;
  // Use Zustand store instead of Redux selector
  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    path: state.path,
    name: state.name,
  }));
  const updateDefaultLandingPage = useDefaultLandingPageStore(
    (state) => state.updateDefaultLandingPage,
  );
  const [choice, setChoice] = useState(CHOICE.new);

  const isSaved = pageType === PAGE_TYPE.SAVED_DASHBOARD;
  const isShared = pageType === PAGE_TYPE.SHARING;

  if (isShared) {
    id = get(props, ['match', 'params', 'id'], null);
  }

  const isDefaultLandingPage = useMemo(() => {
    const { pathname } = window.location;
    const defaultPathname = defaultLandingPage.url || '';
    return defaultPathname.endsWith(pathname);
  }, [defaultLandingPage.url]);

  // if the legacy dashboard was set as the default dashboard
  // reset
  useEffect(() => {
    if (isDefaultLandingPage) {
      const payload = { url: '', name: '', path: '' };
      setLandingPage(payload).then(() => {
        updateDefaultLandingPage(payload);
      });
    }
  }, [isDefaultLandingPage]);

  const handleChoice = (choice) => {
    apptracker(ERROR_DIAG_MIGRATION, { choice });
    setChoice(choice);
  };
  const resetChoice = () => setChoice('');

  const fallback = () => {
    message.error('There was an error while generating new compare dashboard.');
    resetChoice();
  };

  if (!props) {
    return <div>loading....</div>;
  }

  return (
    <div>
      <Result
        status="info"
        icon={<UpCircleOutlined />}
        title="Upgrade this dashboard to New Error Diagnostics."
        subTitle={
          <Space direction="vertical">
            <div>New Error Diagnostics dashboard ... placeholder ...</div>
            <div>Legacy Error Diagnostics dashboard will be deprecated very soon.</div>
          </Space>
        }
        extra={[
          choice ? (
            <NewErrorDashboard
              {...props}
              fallback={fallback}
              isSaved={isSaved}
              isShared={isShared}
              id={id}
            />
          ) : (
            <div>
              <StyledCarousel dotPosition="top" autoplay>
                <div key="1">
                  <Slide>
                    <Image src={IMG1} />
                  </Slide>
                </div>
              </StyledCarousel>
            </div>
          ),
        ]}
      />
    </div>
  );
}

export default Migrate;
